import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { DatePicker } from '../DatePicker';
import { collectUniqueFilterNames } from '../../utils/collect-unique-filter-names';
import { AdminAccessDenied } from './AdminAccessDenied';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  ADMIN_BADGE_PURPLE_CLASSNAME,
  ADMIN_BODY_TEXT_CLASSNAME,
  ADMIN_CHART_TOOLTIP_STYLE,
  ADMIN_CHARTS_GRID_CLASSNAME,
  ADMIN_EMPTY_BOX_CLASSNAME,
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_MODAL_CLASSNAME,
  ADMIN_MODAL_OVERLAY_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

interface CreditsActivityItem {
  id: string;
  userId: string;
  userEmail?: string;
  userAdmin?: boolean;
  balance: number;
  createdAt: string;
  usageType: string;
  notes: string | null;
  documentId: string | null;
  documentTitle: string | null;
  projectId: string | null;
  projectTitle: string | null;
  llmCallId: string | null;
  llmCallWordCount: number | null;
  llmCallTokens: number | null;
  llmCallCost: number | null;
}

export const CreditsActivity: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [activity, setActivity] = useState<CreditsActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<CreditsActivityItem | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUsageType, setSelectedUsageType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [excludeAdmin, setExcludeAdmin] = useState<boolean>(true);
  const [excludePositive, setExcludePositive] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalActivities, setTotalActivities] = useState<number>(0);
  const [aggregations, setAggregations] = useState<{
    totalActivities: number;
    totalCreditsUsed: number;
    totalCreditsAdded: number;
    totalTokensUsed?: number;
    totalCostEstimation?: number;
    activitiesByType?: Array<{ name: string; value: number }>;
    creditsByType?: Array<{ name: string; value: number }>;
  } | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [excludeAdmin, excludePositive, selectedUserId, selectedUsageType, startDate, endDate]);

  useEffect(() => {
    const fetchActivity = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: '25',
          excludeAdmin: excludeAdmin.toString(),
        });

        if (selectedUserId) {
          params.append('userId', selectedUserId);
        }
        if (startDate) {
          params.append('startDate', startDate);
        }
        if (endDate) {
          params.append('endDate', endDate);
        }
        if (selectedUsageType) {
          params.append('usageType', selectedUsageType);
        }

        const url = masterKey
          ? `${API_BASE}/api/admin-credits-activity?${params.toString()}&masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin-credits-activity?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch credits activity');
        }

        const data = await response.json();
        setActivity(data.activity || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalActivities(data.pagination?.total || 0);
        setAggregations(data.aggregations || null);
        setError(null);
      } catch (err) {
        console.error('Error fetching credits activity:', err);
        setError('Error al cargar actividad de créditos');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [token, user, masterKey, currentPage, excludeAdmin, selectedUserId, selectedUsageType, startDate, endDate]);

  const getUsageTypeLabel = useCallback((usageType: string): string => {
    const normalizedUsageType = usageType.replace(/-/g, '_');
    const translationKey = `usage_type_${normalizedUsageType}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : usageType;
  }, [t]);

  const uniqueUsers = React.useMemo(() => {
    const usersMap = new Map<string, string>();
    activity.forEach((item) => {
      if (item.userId && item.userEmail) {
        usersMap.set(item.userId, item.userEmail);
      }
    });
    return Array.from(usersMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [activity]);

  const uniqueUsageTypes = React.useMemo(() => {
    return collectUniqueFilterNames(
      aggregations?.activitiesByType,
      activity.map((item) => item.usageType)
    );
  }, [aggregations, activity]);

  const filteredActivity = React.useMemo(() => {
    let result = activity;
    if (selectedUserId) {
      result = result.filter((item) => item.userId === selectedUserId);
    }
    if (selectedUsageType) {
      result = result.filter((item) => item.usageType === selectedUsageType);
    }
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((item) => {
        const itemDate = new Date(item.createdAt);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= start;
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate <= end;
      });
    }
    if (excludePositive) {
      result = result.filter((item) => item.balance <= 0);
    }
    return result;
  }, [activity, selectedUserId, selectedUsageType, startDate, endDate, excludePositive]);

  const activitiesByTypeData = useMemo(() => {
    return aggregations?.activitiesByType || [];
  }, [aggregations]);

  const creditsByTypeData = useMemo(() => {
    return aggregations?.creditsByType || [];
  }, [aggregations]);

  const COLORS = ['var(--scribane-black)', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#3b82f6', '#f97316', '#06b6d4'];

  const getCurrentIndex = useCallback((): number => {
    if (!selectedActivity) return -1;
    return filteredActivity.findIndex(item => item.id === selectedActivity.id);
  }, [selectedActivity, filteredActivity]);

  const navigateToActivity = useCallback((direction: 'prev' | 'next'): void => {
    const currentIndex = getCurrentIndex();

    if (currentIndex === -1) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
    } else {
      targetIndex = currentIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= filteredActivity.length) return;

    setSelectedActivity(filteredActivity[targetIndex]);
  }, [getCurrentIndex, filteredActivity]);

  const currentIndex = getCurrentIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < filteredActivity.length - 1;

  useEffect(() => {
    if (!selectedActivity) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToActivity('prev');
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToActivity('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedActivity, hasPrevious, hasNext, navigateToActivity]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const totalCreditsUsed = aggregations?.totalCreditsUsed || 0;
  const totalCreditsAdded = aggregations?.totalCreditsAdded || 0;

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        <div className={ADMIN_FILTER_BAR_CLASSNAME}>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <label htmlFor="user-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('filter_by_user')}:
              </label>
              <select
                id="user-filter"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className={ADMIN_INLINE_SELECT_CLASSNAME}
              >
                <option value="">{t('all_users')}</option>
                {uniqueUsers.map(([userId, userEmail]) => (
                  <option key={userId} value={userId}>
                    {userEmail}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="type-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('filter_by_usage_type')}:
              </label>
              <select
                id="type-filter"
                value={selectedUsageType}
                onChange={(e) => setSelectedUsageType(e.target.value)}
                className={ADMIN_INLINE_SELECT_CLASSNAME}
              >
                <option value="">{t('all_usage_types')}</option>
                {uniqueUsageTypes.map((type) => (
                  <option key={type} value={type}>
                    {getUsageTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="start-date-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('start_date')}:
              </label>
              <DatePicker
                id="start-date-filter"
                value={startDate}
                onChange={setStartDate}
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="end-date-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('end_date')}:
              </label>
              <DatePicker
                id="end-date-filter"
                value={endDate}
                onChange={setEndDate}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="exclude-admin-filter"
                checked={excludeAdmin}
                onCheckedChange={(checked) => setExcludeAdmin(checked === true)}
              />
              <Label htmlFor="exclude-admin-filter" className="cursor-pointer text-sm text-muted-foreground">
                {t('exclude_admin')}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="exclude-positive-filter"
                checked={excludePositive}
                onCheckedChange={(checked) => setExcludePositive(checked === true)}
              />
              <Label htmlFor="exclude-positive-filter" className="cursor-pointer text-sm text-muted-foreground">
                {t('exclude_positive')}
              </Label>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('total_activities')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{aggregations?.totalActivities || 0}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('credits_used')}</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-destructive`}>{totalCreditsUsed.toLocaleString()}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('credits_added')}</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>{totalCreditsAdded.toLocaleString()}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('tokens_used')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{(aggregations?.totalTokensUsed || 0).toLocaleString()}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('cost_estimation')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>${(aggregations?.totalCostEstimation || 0).toFixed(4)}</div>
          </div>
        </div>

        <div className={ADMIN_CHARTS_GRID_CLASSNAME}>
          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
              {t('activities_by_type')}
            </h2>
            {activitiesByTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={activitiesByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {activitiesByTypeData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...ADMIN_CHART_TOOLTIP_STYLE}
                    formatter={(value) => [`${typeof value === 'number' ? value : 0}`, t('total_activities')]}
                    labelFormatter={(label: string) => `Tipo: ${getUsageTypeLabel(label)}`}
                  />
                  <Legend formatter={(value) => getUsageTypeLabel(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
                No hay datos
              </div>
            )}
          </div>

          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
              {t('credits_by_type')}
            </h2>
            {creditsByTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={creditsByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {creditsByTypeData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...ADMIN_CHART_TOOLTIP_STYLE}
                    formatter={(value) => [`${typeof value === 'number' ? value.toLocaleString() : 0}`, t('credits_used')]}
                    labelFormatter={(label: string) => `Tipo: ${getUsageTypeLabel(label)}`}
                  />
                  <Legend formatter={(value) => getUsageTypeLabel(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
                No hay datos
              </div>
            )}
          </div>
        </div>

        {filteredActivity.length === 0 ? (
          <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
            <AdminEmptyState>{t('no_credits_activity_registered')}</AdminEmptyState>
          </div>
        ) : (
          <>
          <AdminTableWrap>
              <table className="admin-data-table" style={{ minWidth: '1600px' }}>
                <thead>
                  <tr>
                    <th>
                      {t('date')}
                    </th>
                    <th>
                      {t('user')}
                    </th>
                    <th>
                      {t('type')}
                    </th>
                    <th>
                      {t('balance')}
                    </th>
                    <th>
                      {t('document')}
                    </th>
                    <th>
                      {t('project')}
                    </th>
                    <th>
                      {t('notes')}
                    </th>
                    <th>
                      Palabras
                    </th>
                    <th>
                      {t('tokens_used')}
                    </th>
                    <th>
                      {t('cost_estimation')}
                    </th>
                    <th>
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivity.map((item) => (
                    <tr
                      key={item.id}
                    >
                      <td className="admin-table-cell-muted">
                        {new Date(item.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td>
                        {item.userEmail || '-'}
                      </td>
                      <td>
                        <span className={ADMIN_BADGE_PURPLE_CLASSNAME}>
                          {getUsageTypeLabel(item.usageType)}
                        </span>
                      </td>
                      <td className={`font-medium ${item.balance < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {item.balance > 0 ? '+' : ''}{item.balance.toLocaleString()}
                      </td>
                      <td className="admin-table-cell-muted">
                        {item.documentTitle || '-'}
                      </td>
                      <td className="admin-table-cell-muted">
                        {item.projectTitle || '-'}
                      </td>
                      <td className="admin-table-cell-muted max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {item.notes || '-'}
                      </td>
                      <td className="admin-table-cell-muted">
                        {item.llmCallId && item.llmCallWordCount !== null ? item.llmCallWordCount.toLocaleString() : '-'}
                      </td>
                      <td className="admin-table-cell-muted">
                        {item.llmCallTokens !== null ? item.llmCallTokens.toLocaleString() : '-'}
                      </td>
                      <td className="admin-table-cell-muted">
                        {item.llmCallCost !== null ? `$${item.llmCallCost.toFixed(4)}` : '-'}
                      </td>
                      <td>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setSelectedActivity(item)}
                        >
                          {t('view_details')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrap>
            <AdminPaginationBar
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalActivities}
            />
          </>
        )}
      </div>

      {selectedActivity && (
        <div
          className={ADMIN_MODAL_OVERLAY_CLASSNAME}
          onClick={() => setSelectedActivity(null)}
        >
          <div
            className={ADMIN_MODAL_CLASSNAME}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {hasPrevious && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => navigateToActivity('prev')}
                  >
                    ← Anterior
                  </Button>
                )}
                {hasNext && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => navigateToActivity('next')}
                  >
                    Siguiente →
                  </Button>
                )}
              </div>
              <h2 className="m-0 text-lg font-semibold text-foreground">{t('activity_details')}</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedActivity(null)}
              >
                ×
              </Button>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('date')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {new Date(selectedActivity.createdAt).toLocaleString('es-ES')}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('user')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>{selectedActivity.userEmail || 'N/A'}</div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('usage_type')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>{getUsageTypeLabel(selectedActivity.usageType)}</div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('balance')}</div>
              <div className={`text-base font-medium ${selectedActivity.balance < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {selectedActivity.balance > 0 ? '+' : ''}{selectedActivity.balance.toLocaleString()}
              </div>
            </div>

            {selectedActivity.documentTitle && (
              <div className="mb-6">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('document')}</div>
                <div className={ADMIN_BODY_TEXT_CLASSNAME}>{selectedActivity.documentTitle}</div>
              </div>
            )}

            {selectedActivity.projectTitle && (
              <div className="mb-6">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('project')}</div>
                <div className={ADMIN_BODY_TEXT_CLASSNAME}>{selectedActivity.projectTitle}</div>
              </div>
            )}

            {selectedActivity.notes && (
              <div className="mb-6">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('notes')}</div>
                <div className="max-h-[200px] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-4 font-mono text-sm text-foreground">
                  {selectedActivity.notes}
                </div>
              </div>
            )}

            {selectedActivity.llmCallId && (
              <div className="mb-6">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('llm_call')}</div>
                <Button
                  asChild
                  size="sm"
                >
                  <Link
                    to={`/admin/llm-calls?callId=${selectedActivity.llmCallId}`}
                    onClick={() => setSelectedActivity(null)}
                  >
                    {t('view_llm_call')}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

